import API_BASE from '../utils/apiBase';

const DEFAULT_API = API_BASE;

/**
 * Resolve a relative or absolute API path to a full fetch URL.
 */
export function resolveApiPath(url, apiBase = DEFAULT_API) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  const base = String(apiBase || DEFAULT_API).replace(/\/+$/, '');

  if (trimmed.startsWith('/api/')) {
    return `${base}${trimmed.substring(4)}`;
  }

  if (trimmed.startsWith('/')) {
    const origin = base.replace(/\/api$/, '');
    return `${origin}${trimmed}`;
  }

  return trimmed;
}

function isPlaceholderDocumentId(id) {
  if (!id) return true;
  const value = String(id).trim();
  if (!value) return true;
  return value.endsWith('_canonical');
}

function pickDocumentId(doc) {
  return doc?.document_id || doc?.file_id || doc?.id || null;
}

function shouldPreferStamped(doc, options = {}) {
  if (options.preferStamped === false) return false;
  return Boolean(
    doc?.verified
    || (doc?.verification_stamp && doc.verification_stamp !== 'not_verified')
    || doc?.stamped_file_url
    || doc?.status === 'verified'
    || doc?.status === 'approved',
  );
}

function isHistoryOnlyTraining(item) {
  // employee_training is a competency/history record, not necessarily a file.
  // A synthetic "training-<id>" row must never be offered as a PDF.
  return item?.source === 'training'
    && !item?.source_document_id
    && !item?.document_id
    && !item?.view_url
    && !item?.fetch_url
    && !item?.download_url
    && !item?.file_url
    && !item?.url
    && !item?.storage_key;
}

/**
 * Normalize any document/file reference into an authenticated fetch URL.
 */
export function resolveDocumentFetchUrl(doc, options = {}) {
  const apiBase = options.apiBase || DEFAULT_API;
  if (!doc || isHistoryOnlyTraining(doc)) return null;

  if (doc.view_url) {
    return resolveApiPath(doc.view_url, apiBase);
  }
  if (doc.fetch_url) {
    return resolveApiPath(doc.fetch_url, apiBase);
  }

  const docId = pickDocumentId(doc);
  if (docId && !isPlaceholderDocumentId(docId)) {
    const suffix = shouldPreferStamped(doc, options) ? 'download' : 'file';
    const base = String(apiBase).replace(/\/+$/, '');
    return `${base}/employee-documents/${docId}/${suffix}`;
  }

  const rawUrl = doc.download_url || doc.file_url || doc.url;
  return resolveApiPath(rawUrl, apiBase);
}

/**
 * Resolve a download URL (always prefers stamped/verified copy when available).
 */
export function resolveDocumentDownloadUrl(doc, options = {}) {
  const apiBase = options.apiBase || DEFAULT_API;
  if (isHistoryOnlyTraining(doc)) return null;

  if (doc?.download_url) {
    return resolveApiPath(doc.download_url, apiBase);
  }

  const docId = pickDocumentId(doc);
  if (docId && !isPlaceholderDocumentId(docId)) {
    const base = String(apiBase).replace(/\/+$/, '');
    return `${base}/employee-documents/${docId}/download`;
  }

  return resolveDocumentFetchUrl(doc, { ...options, preferStamped: true });
}

/**
 * Requirement drawer downloads must use an authorised CareDock endpoint.
 * A historical permanent public Storage URL cannot be treated as a usable
 * download after private-bucket cutover. Without a real document ID, fail
 * closed rather than sending an app bearer token to an untrusted URL.
 */
export function resolveRequirementFileDownloadUrl(file, options = {}) {
  if (!file) return null;
  const apiBase = String(options.apiBase || DEFAULT_API).replace(/\/+$/, '');
  const docId = file.document_id || file.source_document_id || file.file_id;
  // Training history-only rows can have a synthetic record ID. A real
  // source document ID is the condition for an authenticated file route.
  if (isHistoryOnlyTraining(file) && !file.file_id) return null;
  if (docId && !isPlaceholderDocumentId(docId)) {
    return `${apiBase}/employee-documents/${encodeURIComponent(String(docId))}/download`;
  }
  const backendRoute = file.download_url || file.view_url || file.fetch_url;
  // An externally supplied absolute URL is not an authenticated CareDock route.
  if (backendRoute && String(backendRoute).startsWith('/')) {
    return resolveApiPath(backendRoute, apiBase);
  }
  return null;
}

/**
 * Privileged requirement previews must not send an application token to
 * a historical absolute Storage URL or third-party origin.
 * Use the same tenant-authorised CareDock file routes as downloads.
 */
export function resolveRequirementFilePreviewUrl(file, options = {}) {
  if (!file) return null;
  const apiBase = String(options.apiBase || DEFAULT_API).replace(/\/+$/, '');
  const docId = file.document_id || file.source_document_id || file.file_id;
  if (isHistoryOnlyTraining(file) && !file.file_id) return null;
  if (docId && !isPlaceholderDocumentId(docId)) {
    const action = shouldPreferStamped(file) ? 'download' : 'file';
    return `${apiBase}/employee-documents/${encodeURIComponent(String(docId))}/${action}`;
  }
  const backendRoute = file.view_url || file.fetch_url || file.download_url;
  return backendRoute && String(backendRoute).startsWith('/') && !String(backendRoute).startsWith('//')
    ? resolveApiPath(backendRoute, apiBase)
    : null;
}

/**
 * Whether a document row has enough metadata to attempt a preview.
 */
export function canViewDocument(doc) {
  if (!doc || isHistoryOnlyTraining(doc)) return false;
  if (pickDocumentId(doc) && !isPlaceholderDocumentId(pickDocumentId(doc))) return true;
  return Boolean(doc.view_url || doc.fetch_url || doc.download_url || doc.file_url || doc.url);
}